// Ultimate Card Game (UCG) - Bot AI wave planner
//
// Decides WHEN the bot may play cards: stream (every affordable card at
// once, the Easy/Normal pattern), or build up plasma and release it as a
// wave. In this income-bound game every tier already spends its whole
// income, so the only thing a smarter bot can change is timing - and saving
// up into waves was the one quality lever that measured: 65% vs the
// streaming bot over 240 headless matches, with fewer stalled games
// (docs/AI_OPPONENT_IMPLEMENTATION_PLAN_2026-09-22.md, "Stand nach Phase 2").
//
// Postures per decision tick:
//   stream  no wave config - play freely
//   defend  the enemy outweighs us in our own half - play at once
//   push    a released wave - play freely with a raised burst cap
//   build   saving - draws only, no plays
//
// When to leave `build` is either a fixed plasma threshold (the shipped
// tiers) or, with `config.estimator`, a prediction: release as soon as the
// cards in hand that the saved plasma pays for would WIN the fight against
// what is on the board (js/engine/ai/battleEstimator.js), within a minimum and
// a maximum of plasma (docs/AI_MASTERPLAN_2026-10.md, Phase 1.2).
//
// Reads only what the bot may know: undetected cloaked enemies don't count.

import { BoardView, enemyTeamOf } from './perception.js';
import { describeBoard, describeCard, estimateBattle } from './battleEstimator.js';

export const WAVE_POSTURE = Object.freeze({
  STREAM: 'stream',
  DEFEND: 'defend',
  PUSH: 'push',
  BUILD: 'build'
});

// Burst cap while a wave is released (the measured prototype's value).
const PUSH_BURST_CAP = 6;
// Release immediately this close to the plasma cap - never waste income.
const CAP_MARGIN = 5;

// Estimator release (`waves.estimator`; `true` takes all defaults):
//   margin      release when the predicted strength ratio of board + wave
//               against the enemy board reaches this
//   minPlasma   never release a wave smaller than this
//   maxPlasma   always release at this much spendable plasma
//   horizon     seconds a freshly played unit may need to reach the front
//               and still count (a unit played at the base takes ~20 s)
//   params      overrides for the estimator's own parameters
export const ESTIMATOR_RELEASE_DEFAULTS = Object.freeze({
  margin: 1.5,
  minPlasma: 14,
  maxPlasma: 50,
  horizon: 22,
  params: {}
});

// Units appear 130-160 px from their base (js/engine/battlefield.js spawnUnit).
const SPAWN_OFFSET_PX = 145;

// What a plan and the bot's mood ask of the planner: BotAI.waveMods(), or just the strategist's for a bare bot.
function waveModsOf(bot) {
  return bot.waveMods ? bot.waveMods() : (bot.strategist?.waveMods?.() ?? null);
}

export class WavePlanner {
  constructor(bot, config = null) {
    this.bot = bot;
    this.config = config;
    this.reset();
  }

  setConfig(config) {
    if (config && config.estimator) {
      const given = config.estimator === true ? {} : config.estimator;
      config = { ...config, estimator: { ...ESTIMATOR_RELEASE_DEFAULTS, ...given } };
    }
    this.config = config;
    this.nextThreshold = null;
  }

  reset() {
    // How waves were released this match, for diagnostics and A/B reports.
    this.releases = { cap: 0, max: 0, estimator: 0, threshold: 0 };
    this.lastEstimate = null;
    this.releaseUntil = 0;
    this.nextThreshold = null;
    this.opened = false;
    this._recentEnemyValue = 0;
    this._recentEnemyValueTime = 0;
    this.posture = this.config ? WAVE_POSTURE.BUILD : WAVE_POSTURE.STREAM;
  }

  // Value on each side of the midline that matters for defending: the
  // enemy's in our half against ours there (the shared scan in
  // js/engine/ai/perception.js - `view` is this tick's BoardView).
  ownHalfBalance(view = null) {
    const t = (view || new BoardView(this.bot)).tactical;
    return { enemy: t.enemyInOwnHalf, own: t.ownInOwnHalf };
  }

  // The cards in hand the saved plasma would pay for, best value for the
  // plasma first: what a release right now would put on the field.
  pickWave(spendable) {
    const { cardSystem, battlefield, team } = this.bot;
    const hand = cardSystem?.hands?.[team];
    if (!hand) return [];
    const baseX = battlefield.bases?.[team]?.x ?? (team === 'western' ? 46 : 1554);
    const spawnX = baseX + (team === 'western' ? SPAWN_OFFSET_PX : -SPAWN_OFFSET_PX);
    const divider = battlefield.territoryDivider ?? battlefield.width / 2;
    const options = [];
    for (const card of hand) {
      if (!card || card.hidden || card.pending) continue;
      if (card.isSpell || card.type === 'Spell' || card.isModule || card.type === 'Module' || card.isBuilding) continue;
      const cost = cardSystem.getEffectiveCost(team, card);
      if (cost > spendable) continue;
      const x = card.deployAtBonusIndicator ? divider : spawnX;
      const rec = describeCard(card, team, x);
      options.push({ rec, cost, value: ((rec.dps + rec.burst / 3) * rec.hp) / Math.max(1, cost) });
    }
    options.sort((a, b) => b.value - a.value);
    const wave = [];
    let left = spendable;
    for (const o of options) {
      if (o.cost > left) continue;
      wave.push(o.rec);
      left -= o.cost;
    }
    return wave;
  }

  // The predicted strength ratio of (own board + the wave) against the
  // visible enemy board.
  waveRatio(spendable) {
    const e = this.config.estimator;
    const { battlefield, team } = this.bot;
    const { own, enemy } = describeBoard(battlefield, team);
    const wave = this.pickWave(spendable);
    const result = estimateBattle([...own, ...wave], enemy, {
      ownTeam: team,
      bases: battlefield.bases,
      params: { horizon: e.horizon, ...e.params }
    });
    this.lastEstimate = { ratio: result.ratio, spendable, waveUnits: wave.length };
    return result.ratio;
  }

  // Leave `build`? The shipped rule is a plasma threshold; with an estimator
  // configured it is a prediction between a minimum and a maximum of plasma.
  shouldRelease(spendable, nearCap, view) {
    const e = this.config.estimator;
    if (!e) {
      const release = spendable >= this.nextThreshold * (waveModsOf(this.bot)?.thresholdMul ?? 1) || nearCap;
      if (release) this.releases[nearCap ? 'cap' : 'threshold']++;
      return release;
    }
    if (nearCap) { this.releases.cap++; return true; }
    if (spendable >= e.maxPlasma) { this.releases.max++; return true; }
    if (spendable < e.minPlasma) return false;
    if (this.waveRatio(spendable) >= e.margin) { this.releases.estimator++; return true; }
    return false;
  }

  // A plan (or the strategist's follow-up) asks for the wave to go out now.
  forceRelease(seconds) {
    this.releaseUntil = Math.max(this.releaseUntil, this.bot.gameTime + seconds);
  }

  rollThreshold() {
    const c = this.config;
    let base = c.threshold;
    // When pushing deep into enemy half, accumulate slightly larger waves for base assault
    const divider = this.bot.battlefield?.territoryDivider;
    const mid = (this.bot.battlefield?.width || 1600) / 2;
    if (divider !== undefined && divider !== null) {
      const pushedDeep = this.bot.team === 'western' ? (divider > mid + 80) : (divider < mid - 80);
      if (pushedDeep) base += 4;
    }
    this.nextThreshold = base + (c.jitter > 0 ? this.bot.rng.next() * c.jitter : 0);
  }

  // `reserve`: plasma the superweapon controller wants kept back this tick;
  // a wave only counts what it may actually spend.
  tick(reserve = 0, view = null) {
    const c = this.config;
    if (!c) {
      this.posture = WAVE_POSTURE.STREAM;
      return { posture: this.posture, allowPlays: true, burstCap: null };
    }

    const { gameTime, economy, team } = this.bot;
    const battlefield = this.bot.battlefield;
    view = view || new BoardView(this.bot);
    const { enemy, own } = this.ownHalfBalance(view);
    const mods = waveModsOf(this.bot);
    if (enemy > 0 && enemy > own * c.defendFactor * (mods?.defendFactorMul ?? 1)) {
      this.posture = WAVE_POSTURE.DEFEND;
      return { posture: this.posture, allowPlays: true, burstCap: null };
    }

    if (gameTime < this.releaseUntil) {
      this.posture = WAVE_POSTURE.PUSH;
      return { posture: this.posture, allowPlays: true, burstCap: PUSH_BURST_CAP };
    }

    if (this.nextThreshold === null) this.rollThreshold();
    const res = economy.resources[team];
    const spendable = (res?.plasma || 0) - reserve;
    const nearCap = (res?.plasma || 0) >= (res?.maxPlasma || Infinity) - CAP_MARGIN;

    // Emergency Response: if a friendly unit is currently disabled by an enemy Hacker
    // and we have enough plasma to deploy our counter, react immediately!
    const hasDisabledAlly = battlefield?.units?.some(u => !u.isDead && u.team === team && u.isDisabled && (u.disabledByHackerId || u.isHacked));
    if (hasDisabledAlly && spendable >= 14) {
      this.posture = WAVE_POSTURE.DEFEND;
      return { posture: this.posture, allowPlays: true, burstCap: 1 };
    }

    // Opportunistic Breakout: if clustered/rooted enemies are immobilized in front of our forces,
    // release burst to capitalize immediately (e.g. Wheelie Bomber on Harvesting Pillar cluster)
    const hasRootedCluster = battlefield?.units?.some(u => !u.isDead && u.team !== team && u.isRooted);
    if (hasRootedCluster && spendable >= 10) {
      this.posture = WAVE_POSTURE.PUSH;
      return { posture: this.posture, allowPlays: true, burstCap: 2 };
    }

    // 1. Opening Cadence: in the first 10 seconds of a match, if the bot has 0 units on field,
    // allow playing 1 cheap frontline unit to contest the midline instead of remaining paralyzed.
    if (!this.opened && gameTime < 10 && own === 0 && spendable >= 6) {
      this.opened = true;
      this.posture = WAVE_POSTURE.BUILD;
      return { posture: this.posture, allowPlays: true, burstCap: 1, isOpening: true };
    }

    // 2. Approaching Rush & Territory Divider Contestation:
    // - If fast enemy sprinters (speed >= 3.0) are closing in on the midline while we have 0 defenders, OR
    // - If the enemy has pushed the territoryDivider into our half (giving them free bonus income)
    //   and outweighs our board presence while we have >= 14 plasma, contest the frontline immediately!
    if (battlefield && spendable >= 6) {
      const mid = battlefield.width / 2;
      const isEastern = team === 'eastern';
      if (own === 0) {
        const fastApproaching = battlefield.units.some(u => {
          if (u.isDead || u.team === team || u.isCloaked || (u.speed || 0) < 3.0) return false;
          return isEastern ? (u.x > mid - 250) : (u.x < mid + 250);
        });
        if (fastApproaching) {
          this.posture = WAVE_POSTURE.DEFEND;
          return { posture: this.posture, allowPlays: true, burstCap: 1 };
        }
      }

      const divider = battlefield.territoryDivider;
      const losingTerritory = (divider !== undefined && divider !== null)
        ? (isEastern ? (divider > mid + 25) : (divider < mid - 25))
        : false;
      if (losingTerritory && spendable >= 14) {
        // Whole-map value on each side, from the same scan.
        const { enemyValue: totalEnemyVal, ownValue: totalOwnVal } = view.tactical;
        if (totalEnemyVal > totalOwnVal) {
          this.posture = WAVE_POSTURE.DEFEND;
          return { posture: this.posture, allowPlays: true, burstCap: 2 };
        }
      }
    }

    // Punish & Opportunistic Breakout (Section 5.2):
    // 1. Enemy spent plasma and our half is clear (or enemy deployed an unescorted Salvage Company with low plasma)
    // 2. Sudden collapse of enemy frontline value (>= 30 plasma drop within 3.5s) while we hold plasma
    const enemyPlasma = (this.bot.belief ? this.bot.belief.enemyPlasma() : economy.resources[enemyTeamOf(team)]?.plasma) ?? 50;
    const currentEnemyValue = view.tactical.enemyValue || 0;
    const opportunism = this.bot.profile?.opportunism ?? 0.8;
    const enemyCollapsed = opportunism >= 0.4 && (this._recentEnemyValue - currentEnemyValue >= 30) && (gameTime - this._recentEnemyValueTime <= 3.5);
    if (gameTime - this._recentEnemyValueTime > 2.5 || currentEnemyValue > this._recentEnemyValue) {
      this._recentEnemyValue = currentEnemyValue;
      this._recentEnemyValueTime = gameTime;
    }
    const enemyUnprotectedEco = opportunism >= 0.4 && enemyPlasma <= 10 && battlefield?.units?.some(u => !u.isDead && u.team !== team && (u.cardData?.name === 'The Salvage Company' || u.unitType === 'TheSalvageCompany'));
    if ((c.punish && enemyPlasma <= 10 && enemy === 0 && spendable >= 18) || ((enemyCollapsed || enemyUnprotectedEco) && spendable >= 16)) {
      if (this.bot.pendingCombo?.phase === 'HOLDING_FOR_A' && (enemyCollapsed || enemyUnprotectedEco)) {
        this.bot.pendingCombo = null;
      }
      this.releaseUntil = gameTime + c.releaseSeconds;
      this.rollThreshold();
      this.posture = WAVE_POSTURE.PUSH;
      return { posture: this.posture, allowPlays: true, burstCap: PUSH_BURST_CAP, isOpportunistic: true };
    }

    if (this.shouldRelease(spendable, nearCap, view)) {
      this.releaseUntil = gameTime + c.releaseSeconds;
      this.rollThreshold();
      this.posture = WAVE_POSTURE.PUSH;
      return { posture: this.posture, allowPlays: true, burstCap: PUSH_BURST_CAP };
    }

    this.posture = WAVE_POSTURE.BUILD;
    return { posture: this.posture, allowPlays: false, burstCap: null };
  }
}
